import AddressFormFields from '@/components/AddressFormFields';
import { SVAutocomplete, SVSelect, SVTextInput } from '@/components/core';
import { REGEX } from '@/constants/regex';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import {
  ExternalAccountFormValues,
  ExternalAccountType,
} from '@/types/externalAccounts';
import { SelectOption } from '@/types/inputs';
import { Card, Typography, Grid } from '@mui/material';
import { useFormContext } from 'react-hook-form';

type ExternalAccountFormBasicInfoProps = {
  isEdit: boolean;
  companyId: string;
  companiesList: SelectOption[];
  customerListByName: SelectOption[];
  doesntHaveCustomers: boolean;
};

export default function ExternalAccountFormBasicInfo({
  isEdit,
  companyId,
  companiesList,
  customerListByName,
  doesntHaveCustomers,
}: ExternalAccountFormBasicInfoProps) {
  const { isStaff } = useAuthUser();
  const { watch } = useFormContext<ExternalAccountFormValues>();
  const paymentType = watch('paymentType');
  const bankAddressOptional = paymentType === 'ach' || !paymentType;

  return (
    <Card>
      <Typography variant="h5" mb={2}>
        Basic Info
      </Typography>
      <Grid container spacing={2}>
        {!isEdit && (
          <>
            {isStaff && (
              <Grid size={{ xs: 12, md: 6 }}>
                <SVAutocomplete
                  name="companyId"
                  label="Company"
                  options={companiesList}
                  rules={{ required: true }}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              {doesntHaveCustomers ? (
                <Typography color="error">
                  Customers must be associated to an external bank account and
                  the current selected company has no assigned customers.
                </Typography>
              ) : (
                <SVAutocomplete
                  name="customerId"
                  label="Customer"
                  options={customerListByName}
                  rules={{ required: true }}
                  disabled={!companyId}
                />
              )}
            </Grid>
          </>
        )}
        <Grid size={{ xs: 12, md: 6 }}>
          <SVTextInput
            name="bankName"
            label="Bank Name"
            rules={{ required: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <SVSelect
            name="accountType"
            label="Account Type"
            rules={{ required: true }}
            options={[
              { label: 'Checking', value: ExternalAccountType.CHECKING },
              { label: 'Savings', value: ExternalAccountType.SAVINGS },
              { label: 'Brokerage', value: ExternalAccountType.BROKERAGE },
              { label: 'Other', value: ExternalAccountType.OTHER },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <SVSelect
            name="paymentType"
            label="Payment Type"
            rules={{ required: true }}
            options={[
              { label: 'ACH', value: 'ach' },
              { label: 'Wire', value: 'wire' },
              { label: 'ACH & Wire', value: 'both' },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <SVTextInput
            name="accountNumber"
            label="Account Number"
            rules={{
              required: true,
              pattern: {
                readablePatternName: 'account number',
                regex: REGEX.CHECK_ACCOUNT_NUMBER,
              },
            }}
            disabled={isEdit}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <SVTextInput
            name="confirmAccountNumber"
            label="Confirm Account Number"
            rules={{
              required: true,
              pattern: {
                readablePatternName: 'account number',
                regex: REGEX.CHECK_ACCOUNT_NUMBER,
              },
            }}
            disabled={isEdit}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <SVTextInput
            name="routingNumber"
            label="Routing Number"
            rules={{
              required: true,
              pattern: {
                readablePatternName: 'routing number',
                regex: REGEX.CHECK_US_ROUTING_NUMBER,
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography fontWeight="bold">
            Bank Address {bankAddressOptional ? '(Optional)' : ''}
          </Typography>
          <Typography mb={2}>
            Add a bank address to enable wire transfers to this external
            account. Without it, customers will be limited to ACH transfers
            only.
          </Typography>
          <AddressFormFields
            line1Name="bankAddress.line1"
            line2Name="bankAddress.line2"
            cityName="bankAddress.city"
            stateName="bankAddress.state"
            postalCodeName="bankAddress.postalCode"
            isOptional={bankAddressOptional}
          />
        </Grid>
      </Grid>
    </Card>
  );
}
